"use client";
// ════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY TOÀN CỤC — bắt mọi lỗi render ở các trang dưới root layout.
// Vì sao cần: trước đây CHỈ /accounting có error.tsx; mọi trang khác lỗi → Next prod hiện
// màn TRẮNG generic (chỉ digest) → người dùng tưởng "vỡ app". Boundary này hiện thông báo
// rõ + nút Thử lại + Về Tổng quan, kèm mã sự cố (digest khớp log Railway để lần ra).
// ════════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, Home, LogIn, KeyRound } from "lucide-react";
import { Button, Card, CardContent } from "@/components/ui";
import { clientErrorReporter } from "@/components/ClientErrorReporter";
import { loiKemMaSuCo } from "@/lib/loi/ma-su-co";
import { laLoiLechPhienBan, taiLaiThuCong, tuTaiLaiMotLan } from "@/lib/loi/lech-phien-ban";
import { nenDeVeLoi, phanLoaiCongGac, TIEU_DE_CONG_GAC } from "@/lib/loi/phien-dang-nhap";

/**
 * Hạn chờ của màn "Đang tải lại bản mới…" — quá hạn mà trang vẫn còn đây = lượt tải lại KHÔNG
 * xảy ra ⇒ trả về màn lỗi (có nút bấm) để người dùng còn lối ra. Xem chỗ dùng bên dưới.
 */
const HAN_MAN_CHO_MS = 9_000;

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // TAB CŨ GẶP BẢN MỚI (vé thật /treatment 05/08): sổ mô-đun trong bộ nhớ thiếu đúng mô-đun mà
  // gói RSC hỏi ⇒ `reset()` chỉ dựng lại cây React rồi nổ y hệt = NÚT CHẾT. Phải nạp lại trang.
  const lechPhienBan = laLoiLechPhienBan(error);
  const [dangTaiLai, setDangTaiLai] = useState(false);

  // CỔNG GÁC CHẶN (phiên hụt giữa lúc gửi server action) — KHÔNG phải trang này hỏng. Cổng gác đã
  // viết sẵn câu tiếng Việt cho người dùng và Next lấy nguyên văn làm câu lỗi (xem
  // lib/supabase/middleware.ts), nên ở đây phải HIỆN LẠI ĐÚNG CÂU ĐÓ. Bản trước vứt `error.message`
  // rồi bày câu chung "Trang gặp trục trặc… báo kỹ thuật kèm mã sự cố" — vừa đổ oan cho trang, vừa
  // chỉ sai lối ra (thứ họ cần là ĐĂNG NHẬP LẠI, không phải đi báo kỹ thuật).
  const congGac = phanLoaiCongGac(error);
  const [dangChuyenLogin, setDangChuyenLogin] = useState(false);

  useEffect(() => {
    console.error("[app] màn lỗi:", error?.message, error?.digest);
    // BÁO lỗi render (đã tới boundary) về HỘP LỖI SugaHub.
    // ⚠️ Mã sự cố phải đi trong CÂU LỖI, không chỉ ở `source`: lỗi Server Component ở bản
    // production bị React thay bằng một câu chung chung KHÔNG vết, mà thân vé lại không in
    // `source` ⇒ vé tới nơi là vô phương lần ra (đã trả giá 04/08 — xem lib/loi/ma-su-co.ts).
    // VẪN BÁO vé dù có tự chữa được: tần suất lệch phiên bản là thứ cần thấy để còn biết deploy
    // đang hất tab người dùng nhiều hay ít. Gắn nhãn `lech-phien-ban` cho người đọc vé khỏi
    // chẩn đoán lại từ đầu.
    // ✅ ĐÃ SOI ĐƯỜNG GỬI (05/08) — vé PHẢI đi được trước lượt `reload()` ngay dưới, nếu reporter
    // gom lô thì đúng những vé lệch-phiên-bản (thứ ta cần thấy tần suất) sẽ mất sạch và ta lại
    // tưởng lỗi đã hết. Trong @suga-co/observe-web/client: report() → push() → send() gửi NGAY
    // trong lời gọi bằng navigator.sendBeacon, hụt thì fetch(keepalive:true) — không hàng đợi,
    // không setTimeout, không chờ visibilitychange ⇒ sống qua lượt tải lại.
    // 🚫 HẾT PHIÊN KHÔNG PHẢI BỌ ⇒ KHÔNG ĐẺ VÉ (luật + bằng chứng vé thật ở `nenDeVeLoi`).
    // Ca "máy chủ đăng nhập bận" thì VẪN báo, kèm nhãn để người đọc vé khỏi chẩn đoán lại từ đầu.
    if (nenDeVeLoi(error)) {
      clientErrorReporter.report(loiKemMaSuCo(error), {
        source:
          `error-boundary${lechPhienBan ? ":lech-phien-ban" : ""}${congGac ? `:${congGac}` : ""}` +
          `${error?.digest ? `:${error.digest}` : ""}`,
      });
    }
    if (lechPhienBan && tuTaiLaiMotLan()) setDangTaiLai(true);
  }, [error, lechPhienBan, congGac]);

  // ⚠️ MÀN CHỜ PHẢI CÓ ĐƯỜNG RA — lượt tải lại có thể KHÔNG bao giờ xảy ra: người dùng bấm Esc
  // (Firefox dừng nạp trang), hoặc trang đang mở có chốt `beforeunload` ("phiếu chưa lưu" ở màn
  // nhập điều trị rất dễ có) và người dùng chọn "Ở LẠI". Màn chờ không có một nút nào, nên nếu
  // `dangTaiLai` bật rồi không bao giờ tắt thì spinner quay mãi = NGÕ CỤT, chỉ còn nước đóng tab.
  // Quá hạn mà component này còn sống = trang chưa hề rời đi ⇒ hạ cờ, trả về màn lỗi có nút bấm.
  // (Tải lại thành công thì cả cây bị bỏ, đồng hồ theo đó mất — không cần lo đua.)
  useEffect(() => {
    if (!dangTaiLai) return;
    const dongHo = setTimeout(() => setDangTaiLai(false), HAN_MAN_CHO_MS);
    return () => clearTimeout(dongHo);
  }, [dangTaiLai]);

  // Đang tự nạp bản mới → hiện phản hồi CHỜ, đừng bày màn lỗi (người dùng tưởng hệ hỏng thật).
  if (dangTaiLai) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Card className="max-w-lg animate-fade-in">
          <CardContent className="space-y-3 py-8 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-muted-fg" />
            <h2 className="text-lg font-semibold text-fg">Đang tải lại bản mới…</h2>
            <p className="text-sm text-muted-fg">
              Phần mềm vừa được cập nhật trong lúc bạn đang mở trang. Chờ vài giây, trang sẽ tự mở
              lại — bạn không cần làm gì cả.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <Card className="max-w-lg animate-fade-in">
        <CardContent className="space-y-4 py-8 text-center">
          <div
            className={
              congGac
                ? "mx-auto grid h-14 w-14 place-items-center rounded-full bg-warn/10 text-warn"
                : "mx-auto grid h-14 w-14 place-items-center rounded-full bg-danger/10 text-danger"
            }
          >
            {congGac ? <KeyRound className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />}
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-fg">
              {congGac ? TIEU_DE_CONG_GAC[congGac] : "Đã có lỗi xảy ra"}
            </h2>
            {/* Lệch phiên bản thì lời khuyên "về Tổng quan rồi mở lại" là SAI: bấm menu là điều
                hướng MỀM, sổ mô-đun cũ trong bộ nhớ vẫn nguyên ⇒ mở lại là nổ lại. Chỉ nạp lại
                trang mới chữa được, nên nói đúng thứ cần bấm. */}
            <p className="text-sm text-muted-fg">
              {congGac ? (
                // NGUYÊN VĂN câu của cổng gác — nó đã nói đủ 2 điều người đang làm việc cần biết:
                // thao tác vừa rồi CHƯA được lưu, và phải làm gì tiếp. Chép tay bản thứ hai ở đây là
                // tự tạo hai nguồn sự thật, sửa một nơi quên nơi kia.
                error.message
              ) : lechPhienBan ? (
                <>
                  Phần mềm vừa được cập nhật trong lúc bạn đang mở trang. Hãy bấm <b>Tải lại trang</b>{" "}
                  để lấy bản mới; nếu vẫn lỗi, báo kỹ thuật kèm mã sự cố bên dưới.
                </>
              ) : (
                <>
                  Trang gặp trục trặc khi tải. Hãy bấm <b>Thử lại</b>; nếu vẫn lỗi, quay về Tổng quan
                  rồi mở lại, hoặc báo kỹ thuật kèm mã sự cố bên dưới.
                </>
              )}
            </p>
            {error?.digest ? (
              <p className="text-[11px] text-muted-fg">Mã sự cố: {error.digest}</p>
            ) : null}
          </div>
          <div className="flex items-center justify-center gap-2">
            {congGac === "het-phien" ? (
              // HẾT PHIÊN → chỉ MỘT lối ra: đăng nhập lại. Bày thêm "Thử lại"/"Về Tổng quan" ở đây
              // là bày nút chết — vé đã hết, bấm gì cũng bị cổng gác chặn y hệt.
              // ⚠️ Điều hướng CỨNG (`location.assign`), KHÔNG dùng <Link>: điều hướng mềm giữ nguyên
              // cây React đang hỏng và cả router cache; phải một lượt tải trang thật thì cổng gác mới
              // chạy lại và trang đăng nhập mới sạch.
              <Button
                onClick={() => {
                  setDangChuyenLogin(true);
                  window.location.assign("/login");
                }}
                disabled={dangChuyenLogin}
                size="sm"
              >
                {dangChuyenLogin ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" /> Đang chuyển…
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" /> Đăng nhập lại
                  </>
                )}
              </Button>
            ) : (
              <>
                {/* LỆCH PHIÊN BẢN → PHẢI nạp lại trang, KHÔNG `reset()`: reset chỉ dựng lại cây React
                    (sổ mô-đun vẫn thiếu) và gói RSC cũ vẫn nằm trong router cache của Next ⇒ nổ y hệt
                    = nút chết. Người bấm là thao tác CHỦ ĐỘNG nên bỏ qua ngân sách tự-tải-lại. */}
                <Button onClick={() => (lechPhienBan ? taiLaiThuCong() : reset())} size="sm">
                  <RefreshCw className="h-4 w-4" /> {lechPhienBan ? "Tải lại trang" : "Thử lại"}
                </Button>
                <Link href="/">
                  <Button variant="outline" size="sm">
                    <Home className="h-4 w-4" /> Về Tổng quan
                  </Button>
                </Link>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
